import { useContext, useEffect, useRef } from 'react';

import { RecaptchaLibContext, RecaptchaLibDispatchContext } from '../context/context';
import { loadRecaptcha, unloadRecaptcha } from '../functions/loader';
import { RecaptchaLibToken } from '../types/types';
import { useAppSelector } from '@/store/hooks';

export interface RecaptchaLibLoaderProps {
  scoreBasedSiteKey?: RecaptchaLibToken,
  checkboxFallbackSiteKey?: RecaptchaLibToken,
}

function RecaptchaLibLoader({ scoreBasedSiteKey = null, checkboxFallbackSiteKey = null }: RecaptchaLibLoaderProps) {
  const { loaded, scoreBasedSiteKey: loadedScoreBasedKey, checkboxFallbackSiteKey: loadedCheckboxFallbackKey } = useContext(RecaptchaLibContext);
  const dispatchContext = useContext(RecaptchaLibDispatchContext);
  const showReCaptchaV2 = useAppSelector(state => state.transaction.showReCaptchaV2);
  const currentVersion = useRef(showReCaptchaV2 ? 2 : 3);

  useEffect(() => () => {
    unloadRecaptcha();
  }, []); // Only run on unmount

  // Handle version switching
  useEffect(() => {
    // Only unload if we're switching versions (v3->v2 or v2->v3) and something is already loaded
    const isScoreBasedLoaded = loaded && loadedScoreBasedKey;
    const isCheckboxFallbackLoaded = loaded && loadedCheckboxFallbackKey;
    const isSwitchingToCheckboxFallback = showReCaptchaV2 && isScoreBasedLoaded && currentVersion.current === 3;
    const isSwitchingToScoreBased = !showReCaptchaV2 && isCheckboxFallbackLoaded && currentVersion.current === 2;

    if (isSwitchingToCheckboxFallback ?? isSwitchingToScoreBased) {
      unloadRecaptcha();
      dispatchContext({ loaded: false, scoreBasedToken: null, checkboxFallbackToken: null });
      currentVersion.current = showReCaptchaV2 ? 2 : 3;
      return; // Exit to let next effect run handle the loading
    }

    // Load the appropriate version if not loaded
    const siteKeyToUse = showReCaptchaV2 ? checkboxFallbackSiteKey : scoreBasedSiteKey;
    if (siteKeyToUse && !loaded) {
      dispatchContext(showReCaptchaV2 ? { checkboxFallbackSiteKey } : { scoreBasedSiteKey });

      loadRecaptcha(siteKeyToUse, () => {
        dispatchContext({ loaded: true });
      }, showReCaptchaV2);
    }
  }, [scoreBasedSiteKey, checkboxFallbackSiteKey, loaded, loadedScoreBasedKey, loadedCheckboxFallbackKey, dispatchContext, showReCaptchaV2]);

  return null;
}

export default RecaptchaLibLoader;
